import { useTheme } from "@mui/material/styles";
import { Box, Typography, CircularProgress } from "@mui/material";
import { StatCardType } from "./MyMessagesTypes";

const RING_SIZE = { xs: 56, sm: 64 };
const PROGRESS_THICKNESS = 2.5;

export const StatisticCard = ({
  title,
  value,
  color,
  icon,
  total,
}: Pick<StatCardType, "title" | "value" | "color" | "icon" | "total">) => {
  const theme = useTheme();
  // Parse value for percent or number

  let percent = 0;
  if (title === "Points Rewarded") {
    percent = 100;
  } else if (total && total > 0) {
    percent = Math.round((Number(value) / total) * 100);
  } else {
    percent = 0;
  }

  const ringColor = color || theme.palette.primary.main;
  const backgroundColor = `${ringColor}15`;

  return (
    <Box
      data-cypress="stat-card"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "white",
        borderRadius: { xs: 1.5, sm: 2 },
        p: { xs: 2, sm: 2.5 },
        height: "100%",
        minHeight: { xs: 140, sm: 155 },
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: `1px solid ${theme.palette.grey[200]}`,
        borderTop: `3px solid ${ringColor}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: RING_SIZE,
          height: RING_SIZE,
          borderRadius: "50%",
          bgcolor: backgroundColor,
          color: ringColor,
          mb: { xs: 1.5, sm: 2 },
          position: "relative",
        }}
      >
        <Box
          sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem" }, display: "flex" }}
        >
          {icon}
        </Box>

        <CircularProgress
          variant="determinate"
          value={percent}
          size={RING_SIZE.sm}
          thickness={PROGRESS_THICKNESS}
          sx={{
            color: theme.palette.grey[200],
            position: "absolute",
          }}
        />
        <CircularProgress
          variant="determinate"
          value={percent}
          size={RING_SIZE.sm}
          thickness={PROGRESS_THICKNESS}
          sx={{
            color: ringColor,
            position: "absolute",
            transition: "all 0.5s ease-in-out",
          }}
        />
      </Box>

      <Typography
        variant="h4"
        data-cypress="stat-value"
        sx={{
          fontWeight: 800,
          lineHeight: 1,
          mb: 0.75,
          fontSize: { xs: "1.5rem", sm: "1.75rem" },
        }}
      >
        {value}
        {title !== "Points Rewarded" && (
          <span
            style={{
              fontSize: "0.8rem",
              marginLeft: "0.5rem",
              color: theme.palette.text.secondary,
            }}
          >
            ({Number(value) > 0 ? Math.round((Number(value) / total) * 100) : 0}
            %)
          </span>
        )}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        data-cypress="stat-description"
        sx={{
          textAlign: "center",
          fontWeight: 600,
          fontSize: { xs: "0.7rem", sm: "0.75rem" },
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};
